import { useState, useCallback, useMemo } from 'react'

/**
 * Hook for managing job mapping state
 * Stores mappings as: { [sourceJobId]: { targetId, overrideValues: { email, phone, jobStatus } } }
 */
function useJobMapping() {
  const [jobMappings, setJobMappings] = useState({})
  const [selectedPlatformJobs, setSelectedPlatformJobs] = useState([])

  const clearSelection = useCallback(() => {
    setSelectedPlatformJobs([])
  }, [])

  const addMapping = useCallback(
    (sourceJobIds, targetJobId, overrideValues) => {
      setJobMappings(prev => {
        const newMappings = { ...prev }
        sourceJobIds.forEach(sourceJobId => {
          newMappings[sourceJobId] = {
            targetId: targetJobId,
            overrideValues: overrideValues[sourceJobId] || {},
          }
        })
        return newMappings
      })
      clearSelection()
    },
    [clearSelection],
  )

  const removeMappingsForTarget = useCallback(targetJobId => {
    setJobMappings(prev => {
      const newMappings = {}
      Object.keys(prev).forEach(sourceJobId => {
        if (prev[sourceJobId].targetId !== targetJobId) {
          newMappings[sourceJobId] = prev[sourceJobId]
        }
      })
      return newMappings
    })
  }, [])

  const updateMapping = useCallback((sourceJobId, targetJobId, overrideValues) => {
    setJobMappings(prev => ({
      ...prev,
      [sourceJobId]: {
        targetId: targetJobId,
        overrideValues,
      },
    }))
  }, [])

  const toggleJobSelection = useCallback(jobId => {
    setSelectedPlatformJobs(prev => {
      if (prev.includes(jobId)) {
        return prev.filter(id => id !== jobId)
      }
      return [...prev, jobId]
    })
  }, [])

  const resetJobMappings = useCallback(() => {
    setJobMappings({})
    clearSelection()
  }, [clearSelection])

  const getMappedSourceJobsForTarget = useCallback(
    (targetJobId, sourceJobs = []) => {
      if (!targetJobId) {
        return []
      }

      const sourceJobsById = new Map(sourceJobs.map(job => [job.id, job]))

      return Object.entries(jobMappings)
        .filter(([, mapping]) => mapping.targetId === targetJobId)
        .map(([sourceJobId, mapping]) => ({
          ...sourceJobsById.get(sourceJobId),
          overrideValues: mapping.overrideValues || {},
        }))
    },
    [jobMappings],
  )

  const getMappingBySourceId = useCallback(
    sourceJobId => {
      return jobMappings[sourceJobId] || null
    },
    [jobMappings],
  )

  const mappedSourceJobIds = useMemo(() => Object.keys(jobMappings), [jobMappings])

  const mappedTargetJobIds = useMemo(() => {
    const targetIds = new Set()
    Object.values(jobMappings).forEach(mapping => {
      targetIds.add(mapping.targetId)
    })
    return Array.from(targetIds)
  }, [jobMappings])

  const hasOverrides = useMemo(() => {
    return Object.values(jobMappings).some(mapping => {
      const overrides = mapping.overrideValues || {}
      return Object.values(overrides).some(value => value && value !== 'factset')
    })
  }, [jobMappings])

  return {
    jobMappings,
    selectedPlatformJobs,
    addMapping,
    removeMappingsForTarget,
    updateMapping,
    toggleJobSelection,
    clearSelection,
    resetJobMappings,
    getMappedSourceJobsForTarget,
    getMappingBySourceId,
    mappedSourceJobIds,
    mappedTargetJobIds,
    hasOverrides,
  }
}

export default useJobMapping
